import React from 'react';
import type { ReactNode } from 'react';

import { LinkButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import type { IconProps } from '@atlaskit/icon';

import { buildCreationLink, ConfluencePageSubType } from '@confluence/create-blank-fabric-page';

import { useHTV2State } from '../../useHTV2Store';

import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';

type HTV2CreateContentCTAProps = {
	appearance: 'primary' | 'default';
	children: ReactNode;
	contentType: 'page' | 'whiteboard';
	onClick: () => void;
	source: string;
	icon: React.ComponentType<IconProps>;
};

type CtaComponentProps = { children: ReactNode; onClick: () => void };

const HTV2CreateContentCTA = ({
	appearance,
	children,
	contentType,
	onClick,
	source,
	icon,
}: HTV2CreateContentCTAProps) => {
	const { personalSpaceKey } = useHTV2State();

	const { isTwoPageTypesExperience } = useIsLivePagesFeatureEnabled();

	const getButtonHref = (): string => {
		return buildCreationLink({
			contentType,
			source,
			spaceKey: personalSpaceKey,
			subType: isTwoPageTypesExperience ? ConfluencePageSubType.LIVE : undefined,
		});
	};

	return (
		<LinkButton
			onClick={onClick}
			isDisabled={!Boolean(personalSpaceKey)}
			href={getButtonHref()}
			appearance={appearance}
			iconBefore={icon}
		>
			{children}
		</LinkButton>
	);
};

export const HTV2CreatePageCTA = ({ children, onClick }: CtaComponentProps) => {
	return (
		<HTV2CreateContentCTA
			source="homeOnboardingTakeoverV2CreatePage"
			contentType="page"
			onClick={onClick}
			appearance="primary"
			icon={EditIcon}
		>
			{children}
		</HTV2CreateContentCTA>
	);
};

export const HTV2CreateWhiteboardCTA = ({ children, onClick }: CtaComponentProps) => {
	return (
		<HTV2CreateContentCTA
			source="homeOnboardingTakeoverV2CreateWhiteboard"
			contentType="whiteboard"
			onClick={onClick}
			appearance="default"
			icon={WhiteboardIcon}
		>
			{children}
		</HTV2CreateContentCTA>
	);
};
